import { ReactNode, RefObject, useRef } from 'react';
import { CopyIcon, globalToastService, MaximiseArrowsIcon, Tooltip, TOOLTIP_SURFACE } from '@repo/atomic-components';
import { useHorizontalOverflow } from '@/ui/hooks/useHorizontalOverflow';
import { tableToTsv } from '@/utils/tableToCsv';

export interface MarkdownTableProps {
  children: ReactNode;
  onOpenFullView: (children: ReactNode) => void;
}

const COPY_TABLE_TOOLTIP = 'Copy table';
const OPEN_FULL_VIEW_TOOLTIP = 'Open full view';

const chatMessageIconButtonTooltipClass =
  '!bg-theme-white !border-theme-border-grey !text-theme-black !-translate-y-2.5';

// Matches the copy button under chat messages.
const iconButtonClass =
  'text-theme-text-grey hover:text-theme-black flex h-6 w-6 cursor-pointer items-center justify-center border-none bg-transparent p-0 transition-colors';

export const MarkdownTable = ({ children, onOpenFullView }: MarkdownTableProps) => {
  const { ref, hasOverflow } = useHorizontalOverflow<HTMLDivElement>([children]);
  const tableRef = useRef<HTMLTableElement>(null);
  const copyBtnRef = useRef<HTMLButtonElement>(null);
  const openBtnRef = useRef<HTMLButtonElement>(null);

  const handleCopy = async () => {
    if (!tableRef.current) return;
    const tsv = tableToTsv(tableRef.current);
    await navigator.clipboard.writeText(tsv);
    globalToastService.show({
      closable: true,
      severity: 'success',
      summary: 'Copied to clipboard',
      life: 6000,
    });
  };

  return (
    <div>
      <div data-copy-exclude className="mb-2 flex justify-end gap-2">
        <button
          ref={copyBtnRef}
          type="button"
          onClick={handleCopy}
          aria-label={COPY_TABLE_TOOLTIP}
          className={iconButtonClass}
        >
          <CopyIcon className="overflow-visible" />
        </button>
        <Tooltip
          target={copyBtnRef as RefObject<HTMLElement>}
          content={COPY_TABLE_TOOLTIP}
          position="top"
          className={TOOLTIP_SURFACE}
        />
        {hasOverflow && (
          <>
            <button
              ref={openBtnRef}
              type="button"
              onClick={() => onOpenFullView(children)}
              aria-label={OPEN_FULL_VIEW_TOOLTIP}
              className={iconButtonClass}
            >
              <MaximiseArrowsIcon width={14} height={14} />
            </button>
            <Tooltip
              target={openBtnRef as RefObject<HTMLElement>}
              content={OPEN_FULL_VIEW_TOOLTIP}
              position="top"
              className={chatMessageIconButtonTooltipClass}
            />
          </>
        )}
      </div>
      <div ref={ref} className="border-theme-border-grey mb-4 overflow-x-auto rounded-lg border">
        <table ref={tableRef} className="divide-theme-border-grey min-w-full divide-y">
          {children}
        </table>
      </div>
    </div>
  );
};
